import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BarChart3, Lock } from "lucide-react";
import Sparkline from "@/components/Sparkline";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicScorecard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    // Public endpoint — bypasses AuthContext/api instance so no auth header is sent.
    axios.get(`${BACKEND}/api/public/scorecard/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "This share link is invalid or has been revoked."));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white border border-slate-200 rounded-md p-8 max-w-md text-center">
        <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2"/>
        <div className="font-bold text-slate-900 mb-1">Share link unavailable</div>
        <div className="text-xs text-slate-500">{err}</div>
      </div>
    </div>
  );
  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const k = data.kpis || {};
  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-scorecard">
      <div className="border-b bg-white">
        <div className="max-w-5xl mx-auto p-4 flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-[#005289]"/>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">HCD Contract Central · Shared Report</div>
            <div className="text-lg font-bold text-slate-900">{data.name}</div>
          </div>
          <div className="ml-auto text-[10px] text-slate-400">Generated {new Date(data.generated_at).toLocaleString()}</div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <KPI label="Total Contracts" value={k.total_contracts} trend={data.trends?.new || []} tone="blue"/>
          <KPI label="Executed" value={k.executed} trend={data.trends?.executed || []} tone="green"/>
          <KPI label="In Progress" value={k.in_progress} trend={data.trends?.new || []} tone="amber"/>
        </div>
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-2 border-b text-xs font-bold uppercase tracking-wider text-slate-500">
            {data.name} — group by {data.group_by} · metric {data.metric}
          </div>
          <table className="hcd-table text-xs w-full">
            <thead><tr><th>{data.group_by}</th><th className="text-right">Count</th>{data.metric !== "count" ? <th className="text-right">Value</th> : null}</tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i}><td>{r.key}</td><td className="text-right font-mono">{r.count}</td>
                  {data.metric !== "count" ? <td className="text-right font-mono">{data.metric === "sum_amount" ? `$${(r.value || 0).toLocaleString()}` : `${(r.value || 0).toFixed(1)}d`}</td> : null}
                </tr>
              ))}
              {data.rows.length === 0 ? <tr><td colSpan={3} className="text-center text-slate-400 py-4">No data</td></tr> : null}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-400 text-center">
          This is a read-only view. Revoke the link at any time from the Leadership Dashboard.
        </div>
      </div>
    </div>
  );
}

const TONES = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
};
function KPI({ label, value, trend, tone }) {
  return (
    <div className={`border rounded-md p-3 ${TONES[tone]}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-1">{value ?? "—"}</div>
      <div className="mt-1"><Sparkline values={trend} color="currentColor"/></div>
    </div>
  );
}
